/**
 * A generic builder for constructing objects in a fluent and type-safe manner.
 *
 * @template T - The type of the object to build. Must be a record type.
 */
/** Define the resolver function type */
type ResolverFn<T> = (input: Partial<T>) => Partial<T>
/** Define the builder function type */
type BuilderFn<T extends Record<string, any>, K extends keyof T> = (
    builder: ObjectBuilder<T[K]>
) => ObjectBuilder<T[K]>

export class ObjectBuilder<T extends Record<string, any>> {
    protected data: Partial<T> = {}
    private resolver?: ResolverFn<T>

    constructor(initialState: Partial<T> = {}) {
        this.data = { ...initialState }
    }

    /**
     * Sets a property value or a nested builder function.
     * @param key - The property key.
     * @param valueOrBuilderFn - The value or a builder function.
     * @returns The builder instance.
     */
    set<K extends keyof T>(
        key: K,
        valueOrBuilderFn: T[K] | BuilderFn<T, K> | undefined
    ): this {
        if (typeof valueOrBuilderFn === 'function') {
            // Pass current nested state if exists, or empty object
            const nestedInitialState = this.data[key] as
                | Partial<T[K]>
                | undefined
            const builder = new ObjectBuilder<T[K]>(nestedInitialState)

            // when the value is a function, call it as a builder function
            this.data[key] = (valueOrBuilderFn as BuilderFn<T, K>)(
                builder
            ).build()
        } else {
            // otherwise, assign the value directly
            this.data[key] = valueOrBuilderFn as T[K]
        }
        return this
    }

    /**
     * Sets a resolver function to be applied during the build process.
     * @param resolverFn - The function to resolve/normalize the object state.
     * @returns The builder instance.
     */
    resolveWith(resolverFn: ResolverFn<T>): this {
        this.resolver = resolverFn
        return this
    }

    /**
     * Finalizes and returns the constructed object, applying the resolver if set.
     * @returns The built object.
     */
    build(): T {
        let result = this.data

        if (this.resolver) {
            result = this.resolver(result)
        }

        return { ...result } as T
    }
}
